import { CardData, CardModifier } from '../types';
import { createCard } from './Card';
import { getRng } from '../utils/Random';
import { BALANCE } from '../constants';
import { getCurseById } from './CurseRegistry';

export class Deck {
  drawPile: CardData[] = [];
  hand: CardData[] = [];
  discardPile: CardData[] = [];
  exhaustPile: CardData[] = []; // permanently removed from battle
  permanentExhaust: number[] = []; // permanently removed from run
  private modifiers: Map<number, CardModifier>;

  constructor(numbers?: number[], modifiers?: Map<number, CardModifier>, curses: string[] = []) {
    const deck = numbers ?? [...BALANCE.STARTING_DECK];
    this.modifiers = modifiers ?? new Map();

    const numberCards = deck.map(n => {
      const mod = this.modifiers.get(n);
      return createCard(n, mod ? [this.cloneModifier(mod)] : []);
    });

    const curseCards = curses.map(curseId => {
      const curse = getCurseById(curseId);
      return createCard(0, [], {
        kind: 'curse',
        curseId,
        curseName: curse?.name ?? 'Unknown Curse',
        curseText: curse?.text ?? 'Unknown curse effect.',
      });
    });

    this.drawPile = [...numberCards, ...curseCards];
    this.shuffle();
  }

  shuffle(): void {
    getRng().shuffle(this.drawPile);
  }

  draw(count: number): CardData[] {
    const drawn: CardData[] = [];
    for (let i = 0; i < count; i++) {
      // If draw pile empty, reshuffle discard into draw pile
      if (this.drawPile.length === 0 && this.discardPile.length > 0) {
        this.reshuffleDiscard();
      }
      if (this.drawPile.length === 0) break;
      const card = this.drawPile.pop()!;
      card.slot = 'hand';
      this.hand.push(card);
      drawn.push(card);
    }
    return drawn;
  }

  // Move all hand cards to discard pile (end of turn)
  discardHand(): void {
    while (this.hand.length > 0) {
      const card = this.hand.pop()!;
      this.clearTemporaryModifiers(card);
      card.slot = 'hand';
      this.discardPile.push(card);
    }
  }

  // Permanently exhaust a card (removed from battle entirely)
  exhaust(card: CardData): void {
    // Remove from hand
    const handIdx = this.hand.findIndex(c => c.id === card.id);
    if (handIdx !== -1) this.hand.splice(handIdx, 1);
    // Remove from discard too just in case
    const discIdx = this.discardPile.findIndex(c => c.id === card.id);
    if (discIdx !== -1) this.discardPile.splice(discIdx, 1);
    this.clearTemporaryModifiers(card);
    this.exhaustPile.push(card);
  }

  permanentlyExhaust(card: CardData): void {
    this.exhaust(card);
    if (card.kind === 'number') {
      this.permanentExhaust.push(card.number);
    }
  }

  private reshuffleDiscard(): void {
    this.drawPile = [...this.discardPile];
    this.discardPile = [];
    this.shuffle();
  }

  private cloneModifier(mod: CardModifier): CardModifier {
    return {
      ...mod,
      effect: { ...mod.effect },
    };
  }

  private clearTemporaryModifiers(card: CardData): void {
    card.modifiers = card.modifiers.filter(mod => !mod.temporary);
  }

  get remainingCards(): number {
    return this.drawPile.length;
  }

  get totalInPlay(): number {
    return this.drawPile.length + this.hand.length + this.discardPile.length;
  }
}
